"use client";

import React from "react";
import { Users, House, BookOpen, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
const pathways = [{
  id: 1,
  title: "End-User Support",
  description: "Human-centric help desk for your team, providing the rapid response times startups need to stay productive.",
  icon: "Users",
  link: "/ThePartnershipPage"
}, {
  id: 2,
  title: "Infrastructure & RMM",
  description: "Full system management including our advanced RMM toolset and automated Windows updates for every device.",
  icon: "House",
  link: "/ThePartnershipPage"
}, {
  id: 3,
  title: "O365 & Security",
  description: "Expert cloud environment management with straight-through, zero-markup pricing on all O365 licensing.",
  icon: "BookOpen",
  link: "/TransparencyPage"
}];
const highlights = [{
  pathwayId: 1,
  text: "Unlimited Remote Support"
}, {
  pathwayId: 1,
  text: "Dedicated Tech Partner"
}, {
  pathwayId: 1,
  text: "Custom Onboarding"
}, {
  pathwayId: 2,
  text: "RMM Agent Included"
}, {
  pathwayId: 2,
  text: "Automated Patching"
}, {
  pathwayId: 2,
  text: "Fleet Visibility"
}, {
  pathwayId: 3,
  text: "Zero-Markup Licensing"
}, {
  pathwayId: 3,
  text: "Security Hardening"
}, {
  pathwayId: 3,
  text: "Tenant Management"
}];
const iconMap: Record<string, React.ElementType> = {
  Users,
  House,
  BookOpen
};
export default function LearningPathways() {
  return <section data-section-id="224341" id="your-tech-stack-managed" className="relative w-full bg-background py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.05]" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Your Tech Stack, Managed
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            We provide fractional IT services designed specifically for the pace of startups. Flat upfront pricing with deep technical expertise and a true partnership approach.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pathways.map((pathway, index) => {
          const Icon = iconMap[pathway.icon];
          const pathwayHighlights = highlights.filter(h => h.pathwayId === pathway.id);
          return <div key={pathway.id} data-index={index} className="group relative rounded-3xl p-[1px] bg-gradient-to-b from-border/50 to-transparent overflow-hidden">
                {}
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-700 blur-2xl" />

                <Card className="relative h-full min-h-[420px] bg-card/80 backdrop-blur-xl border-none rounded-[calc(var(--radius-3xl)-1px)] transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-2xl group-hover:shadow-primary/10 flex flex-col overflow-hidden">
                  
                  {}
                  <CardHeader className="flex-1 relative z-10 transition-opacity duration-500 group-hover:opacity-0">
                    <div className="size-16 rounded-2xl bg-muted flex items-center justify-center mb-6 border border-border transition-colors duration-500 group-hover:border-primary/50">
                      <Icon className="size-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-2xl font-default text-foreground mb-3">
                      {pathway.title}
                    </CardTitle>
                    <CardDescription className="text-base font-sans text-muted-foreground">
                      {pathway.description}
                    </CardDescription>
                  </CardHeader>

                  {}
                  <div className="absolute inset-x-0 top-0 bottom-[80px] z-20 bg-card/95 backdrop-blur-md p-8 flex flex-col justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <h4 className="text-lg font-semibold font-default text-primary mb-6 flex items-center gap-2 uppercase tracking-wider">
                      <Sparkles className="size-5" /> Service Specs
                    </h4>
                    <ul className="space-y-4">
                      {pathwayHighlights.map((highlight, hIdx) => <li key={hIdx} data-index={hIdx} className="flex items-center gap-3 text-foreground translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500" style={{
                    transitionDelay: `${hIdx * 150}ms`
                  }}>
                          <div className="size-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--color-primary)]" />
                          <span className="font-mono font-medium text-sm">{highlight.text}</span>
                        </li>)}
                    </ul>
                  </div>

                  {}
                  <CardFooter className="relative z-30 mt-auto pt-6 border-t border-border/50 transition-colors duration-500 group-hover:border-primary/30 bg-card/50 backdrop-blur-sm">
                    <Button asChild variant="ghost" className="w-full justify-between group/btn hover:bg-primary/10 hover:text-primary font-sans">
                      <Link to={pathway.link}>
                        Learn More
                        <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}